// One toast at a time, announced to screen readers, with an optional action such as Retry or Undo.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Icon } from "./Icon.tsx";

interface ToastMessage {
  id: number;
  text: string;
  tone: "info" | "warning";
  action?: { label: string; run: () => void };
}

const ToastContext = createContext<(toast: Omit<ToastMessage, "id">) => void>(() => {});

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const show = useCallback((next: Omit<ToastMessage, "id">) => setToast({ ...next, id: Date.now() }), []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), toast.action ? 10_000 : 5_000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const value = useMemo(() => show, [show]);
  return (
    <ToastContext value={value}>
      {children}
      <div className="cv-toast-host" role="status" aria-live="polite">
        {toast && (
          <div className="cv-toast">
            {toast.tone === "warning" && <Icon name="warning" />}
            <span style={{ flex: 1 }}>{toast.text}</span>
            {toast.action && (
              <button type="button" onClick={() => { toast.action?.run(); setToast(null); }}>{toast.action.label}</button>
            )}
          </div>
        )}
      </div>
    </ToastContext>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
